import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

/**
 * MarkdownRenderer - Renders markdown content with Tailwind styling
 * Supports GitHub Flavored Markdown (tables, task lists, strikethrough)
 */
const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, className = '' }) => {
  return (
    <div
      className={`markdown-content text-sm font-normal leading-relaxed text-text-secondary dark:text-text-dark-secondary ${className}`}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Headings
          h1: ({ children }) => (
            <h1 className="text-2xl font-bold text-text-primary dark:text-text-dark-primary mb-4 mt-6 first:mt-0">
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary mb-3 mt-6 first:mt-0 pb-2 border-b border-border-light dark:border-border-dark">
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-2 mt-4">
              {children}
            </h3>
          ),
          h4: ({ children }) => (
            <h4 className="text-base font-semibold text-text-primary dark:text-text-dark-primary mb-2 mt-3">
              {children}
            </h4>
          ),

          // Paragraphs
          p: ({ children }) => (
            <p className="text-text-secondary dark:text-text-dark-secondary mb-4 leading-relaxed">
              {children}
            </p>
          ),

          // Lists
          ul: ({ children }) => (
            <ul className="list-disc list-inside mb-4 space-y-1 text-text-secondary dark:text-text-dark-secondary ml-2">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal list-inside mb-4 space-y-1 text-text-secondary dark:text-text-dark-secondary ml-2">
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li className="text-text-secondary dark:text-text-dark-secondary">
              {children}
            </li>
          ),

          // Links
          a: ({ href, children }) => (
            <a
              href={href}
              className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 underline"
              target={href?.startsWith('http') ? '_blank' : undefined}
              rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
            >
              {children}
            </a>
          ),

          // Strong and emphasis
          strong: ({ children }) => (
            <strong className="font-semibold text-text-primary dark:text-text-dark-primary">
              {children}
            </strong>
          ),
          em: ({ children }) => (
            <em className="italic">{children}</em>
          ),

          // Code
          code: ({ className, children }) => {
            const isInline = !className;
            if (isInline) {
              return (
                <code className="bg-background-tertiary dark:bg-background-dark-tertiary text-primary-600 dark:text-primary-400 px-1.5 py-0.5 rounded text-sm font-mono">
                  {children}
                </code>
              );
            }
            return (
              <code className={className}>
                {children}
              </code>
            );
          },
          pre: ({ children }) => (
            <pre className="bg-background-tertiary dark:bg-background-dark-tertiary p-4 rounded-lg overflow-x-auto mb-4 text-sm font-mono text-text-primary dark:text-text-dark-primary">
              {children}
            </pre>
          ),

          // Blockquote
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-primary-500 pl-4 italic text-text-secondary dark:text-text-dark-secondary my-4 bg-white dark:bg-background-dark-secondary py-2 rounded-r">
              {children}
            </blockquote>
          ),

          // Horizontal rule
          hr: () => (
            <hr className="border-border-light dark:border-border-dark my-6" />
          ),

          // Tables
          table: ({ children }) => (
            <div className="overflow-x-auto mb-4">
              <table className="min-w-full divide-y divide-border-light dark:divide-border-dark">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            // eslint-disable-next-line no-restricted-syntax -- markdown prose table, document formatting not a data table
            <thead className="bg-white dark:bg-background-dark-secondary">
              {children}
            </thead>
          ),
          tbody: ({ children }) => (
            <tbody className="divide-y divide-border-light dark:divide-border-dark">
              {children}
            </tbody>
          ),
          tr: ({ children }) => (
            <tr>{children}</tr>
          ),
          th: ({ children }) => (
            <th className="px-4 py-3 text-left text-xs font-semibold text-text-primary dark:text-text-dark-primary uppercase tracking-wider">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="px-4 py-3 text-sm text-text-secondary dark:text-text-dark-secondary">
              {children}
            </td>
          ),

          // Images
          img: ({ src, alt }) => (
            <img
              src={src}
              alt={alt}
              className="max-w-full h-auto rounded-lg my-4"
            />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;






